import axios from 'axios';

// Using exchangerate-api.com free tier (no API key required)
// 1500 requests/month free
const API_URL = 'https://api.exchangerate-api.com/v4/latest/GBP';

/**
 * Fetch real-time exchange rates from GBP to other currencies
 * @returns {Promise<{USD: number, EUR: number}>} Exchange rates
 */
export const fetchExchangeRates = async () => {
  try {
    console.log('📊 Fetching exchange rates from API...');
    const response = await axios.get(API_URL, {
      timeout: 5000 // 5 second timeout
    });

    const rates = {
      USD: response.data.rates.USD,
      EUR: response.data.rates.EUR
    };

    console.log('✅ Exchange rates fetched:', rates);
    return rates;
  } catch (error) {
    console.error('❌ Error fetching exchange rates:', error.message);

    // Return fallback rates based on approximate current exchange rates
    console.log('⚠️ Using fallback exchange rates');
    return {
      USD: 1.27, // 1 GBP ≈ 1.27 USD
      EUR: 1.17  // 1 GBP ≈ 1.17 EUR
    };
  }
};

/**
 * Get cached exchange rates from localStorage
 * @returns {{rates: {USD: number, EUR: number}, timestamp: number} | null}
 */
export const getCachedRates = () => {
  try {
    const cached = localStorage.getItem('fxRates');
    if (cached) {
      const data = JSON.parse(cached);
      // Check if cache is less than 1 hour old
      const isValid = Date.now() - data.timestamp < 3600000; // 1 hour
      if (isValid) {
        console.log('✅ Using cached exchange rates');
        return data;
      } else {
        console.log('⏰ Cached rates expired');
      }
    }
  } catch (error) {
    console.error('Error reading cached rates:', error);
  }
  return null;
};

/**
 * Cache exchange rates to localStorage
 * @param {{USD: number, EUR: number}} rates
 */
export const cacheRates = (rates) => {
  try {
    const data = {
      rates,
      timestamp: Date.now()
    };
    localStorage.setItem('fxRates', JSON.stringify(data));
    console.log('💾 Exchange rates cached');
  } catch (error) {
    console.error('Error caching rates:', error);
  }
};

/**
 * Convert amount from GBP to target currency
 * @param {number} amountInGBP Amount in GBP
 * @param {string} targetCurrency Target currency (USD, EUR, or GBP)
 * @param {{USD: number, EUR: number}} rates Exchange rates
 * @returns {number} Converted amount
 */
export const convertCurrency = (amountInGBP, targetCurrency, rates) => {
  if (targetCurrency === 'GBP') {
    return amountInGBP;
  }

  const rate = rates[targetCurrency];
  if (!rate) {
    console.warn(`Unknown currency: ${targetCurrency}, returning GBP amount`);
    return amountInGBP;
  }

  return Math.round(amountInGBP * rate * 100) / 100; // Round to 2 decimal places
};

/**
 * Get currency symbol for display
 * @param {string} currency Currency code (USD, EUR, GBP)
 * @returns {string} Currency symbol
 */
export const getCurrencySymbol = (currency) => {
  const symbols = {
    GBP: '£',
    USD: '$',
    EUR: '€'
  };
  return symbols[currency] || '$';
};
